import { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import type { CameraStreamType } from '@tsr/shared-types';
import { COVERAGE_LIVE } from '@tsr/ui';
import { openPopoutWindow } from './popout';
import { YouTubeLivePlayer } from './YouTubeLivePlayer';

/** Fallback clip re-fetch cadence (seconds) for mp4_clip cameras when the row
 *  carries no metadata.refresh_interval_s. */
const DEFAULT_MP4_CLIP_REFRESH_S = 30;

/** On a fatal HLS error, silently re-mount the source this many times before
 *  surfacing the manual "Retry stream" UI. A re-mount re-fetches /stream, which
 *  for a view-time-resolved source (Verkada) mints a fresh token server-side —
 *  so an expired token refreshes instead of killing the feed. */
const MAX_AUTO_RETRIES = 3;
const AUTO_RETRY_DELAY_MS = 1500;

/** mp4_clip cold-start (Québec 511 / MTQ): the upstream GENERATES the clip on
 *  demand — warm ~0.1s, cold 30s+. The first cold request frequently errors or
 *  stalls while the generator runs, which without special handling reads as a
 *  BROKEN feed (#135). So an mp4_clip error is treated as "still generating" and
 *  re-attempted on a generation-aware cadence before we ever say "offline", and a
 *  generous first-frame deadline bounds a genuinely-dead generator so the spinner
 *  doesn't hang forever. 5 × 6s ≈ 30s of patient retrying spans a cold generate. */
const MP4_COLD_RETRIES = 5;
const MP4_COLD_RETRY_DELAY_MS = 6000;
/** If a cold mp4_clip never reaches 'playing' within this window (even with no
 *  error event — a silent stall), stop waiting and show offline + manual retry. */
const MP4_FIRST_FRAME_TIMEOUT_MS = 45_000;

/** Once an HLS stream reaches 'playing' (currentTime advancing), give it this
 *  long to report a decoded video track. If `videoWidth` is still 0, the
 *  playlist is audio-only / has no video track — we show an honest "no video
 *  track" state instead of a LIVE badge over a permanently black frame
 *  (QA-9 false-live: Bluenose II Wharf). mp4_clip is exempt — it genuinely
 *  decodes video and its periodic in-place reload can read 0×0 for a beat. */
const NO_VIDEO_WATCHDOG_MS = 6000;

/**
 * Human provider label for an mp4_clip camera's on-demand cold-start copy (#386).
 *
 * An mp4_clip is GENERATED upstream on demand, so the loading copy names WHO
 * generates it. That provider is derived from the row's ingestion `source` — NOT
 * the stream_type, which is the SAME ('mp4_clip') for every such cam and so
 * mis-attributes them all to Québec 511 (the QA-8 symptom: the NB CCG e-Nav
 * "Saint John Range" cam captioned "Québec 511"). An unknown source returns null
 * → a source-neutral copy, so a new mp4_clip source can never be silently
 * mislabelled.
 */
export function mp4ClipProviderLabel(source: string | undefined): string | null {
  switch (source) {
    case 'qc511-mtq':
      return 'Québec 511';
    // CCG e-Nav (.mp4 on-demand) rows are catalogued under tsr-catalog and
    // reclassified to mp4_clip by the #367 reclassify — the only mp4_clip path
    // for that source.
    case 'tsr-catalog':
      return 'CCG e-Nav';
    default:
      return null;
  }
}

interface LiveCameraPlayerProps {
  cameraId: string;
  streamUrl: string;
  /** Route through the same-origin API proxy instead of the upstream URL —
   *  used for mixed-content (http://) / CORS-blocked sources. */
  useProxy: boolean;
  name: string;
  /** Ingestion source key of the row (#386) — drives the mp4_clip cold-start copy's
   *  provider label so a non-QC on-demand clip (e.g. NB CCG e-Nav) is not captioned
   *  "Québec 511". Optional: an unknown/absent source falls back to neutral copy. */
  source?: string;
  /** Drives the playback strategy. 'mp4_clip' = short looping MP4 (Québec 511 /
   *  MTQ); everything else uses the HLS path. */
  streamType: CameraStreamType;
  /** mp4_clip only: seconds between client-side clip re-fetches (default 30). */
  refreshIntervalS?: number;
  /** Show the "open in new window" pop-out control (Phase V). Default true; the
   *  pop-out window itself passes false so it doesn't offer to re-pop itself. */
  enablePopout?: boolean;
  /** Wall fill mode (#232): fill the parent edge-to-edge — no card border /
   *  radius / fixed 16:9 aspect, video centre-crops (object-cover) so a mosaic
   *  tile is uniform with no black bars. Also drops our overlay chrome (LIVE
   *  badge, retry buttons — the wall caption owns state and the wall is
   *  read-only). Default false = panel card, unchanged. */
  fill?: boolean;
  /** Reports the player's honest runtime up to a host caption (#232): the wall
   *  tile only captions LIVE while frames are confirmed flowing. */
  onPlaybackState?: (state: 'loading' | 'playing' | 'no_video' | 'offline') => void;
}

type PlayerState = 'loading' | 'playing' | 'error' | 'no_video';

/**
 * Live-video player for a status=live camera, shown in the side panel.
 *
 * Two strategies, chosen by `streamType`:
 * - 'mp4_clip' (Québec 511 / MTQ): a short looping MP4 served at a stable URL.
 *   The browser loops it natively (`loop`); we re-fetch the URL on an interval
 *   to pick up the operator's newer clip. No HLS.js, no manifest. The origin
 *   sends no Access-Control-Allow-Origin, so crossOrigin must stay unset for
 *   these or the media load is blocked (see the <video> below).
 * - everything else (HLS): native HLS on Safari/iOS; hls.js elsewhere.
 *
 * Common behaviour: muted autoplay (browsers block unmuted autoplay), loading
 * spinner during startup, snapshot fallback + retry on fatal error, and full
 * teardown (hls.js + clip timer + cleared <video>) on unmount so we don't leak
 * the stream or hold bandwidth open after the panel closes.
 */
export function LiveCameraPlayer({
  cameraId,
  streamUrl,
  useProxy,
  name,
  source,
  streamType,
  refreshIntervalS,
  enablePopout = true,
  fill = false,
  onPlaybackState,
}: LiveCameraPlayerProps): JSX.Element {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const autoRetryRef = useRef(0);
  const [state, setState] = useState<PlayerState>('loading');
  const [attempt, setAttempt] = useState(0);

  // Honest runtime → host caption (#232): a fatal error reads as offline.
  useEffect(() => {
    if (!onPlaybackState) return;
    onPlaybackState(state === 'error' ? 'offline' : state);
  }, [state, onPlaybackState]);

  const isMp4Clip = streamType === 'mp4_clip';
  // youtube_live takes a wholly different path (IFrame API, not <video>/HLS):
  // delegate to the dedicated player. Hooks above stay mounted but idle.
  const isYouTube = streamType === 'youtube_live';
  const src = useProxy ? `/api/cameras/${cameraId}/stream` : streamUrl;
  const refreshMs = Math.max(5, refreshIntervalS ?? DEFAULT_MP4_CLIP_REFRESH_S) * 1000;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    let destroyed = false;
    setState('loading');

    // No-video watchdog (audio-only / no-video-track HLS), cleared on teardown.
    let noVideoTimer: ReturnType<typeof setTimeout> | undefined;
    const armNoVideoWatchdog = (): void => {
      // HLS-only: mp4_clip genuinely decodes video, and its in-place reload can
      // momentarily read 0×0; only the HLS path has the audio-only failure mode.
      if (isMp4Clip) return;
      if (noVideoTimer) clearTimeout(noVideoTimer);
      noVideoTimer = setTimeout(() => {
        if (destroyed) return;
        // currentTime is advancing (we reached 'playing') yet the decoder reports
        // no video track — an audio-only / no-video HLS playlist. Drop the LIVE
        // badge for an honest state rather than badge a permanently black frame.
        if (video.videoWidth === 0 && !video.paused) setState('no_video');
      }, NO_VIDEO_WATCHDOG_MS);
    };

    // mp4_clip cold-start patience deadline (#135): a silent stall (no error
    // event, just a generator running) shouldn't spin forever. Armed on mount,
    // cleared the moment we reach 'playing'.
    let coldStartTimer: ReturnType<typeof setTimeout> | undefined;
    let reachedPlaying = false;
    const onPlaying = (): void => {
      if (destroyed) return;
      reachedPlaying = true;
      if (coldStartTimer) clearTimeout(coldStartTimer);
      autoRetryRef.current = 0; // a clean start re-arms the auto-retry budget
      setState('playing');
      armNoVideoWatchdog();
    };
    // A real video track arriving late (decoder resize) clears a no_video verdict
    // so a genuinely-slow-but-real stream isn't stuck on the honest fallback.
    const onResize = (): void => {
      if (destroyed) return;
      if (video.videoWidth > 0) setState((s) => (s === 'no_video' ? 'playing' : s));
    };
    // Auto-retry timer (token-refresh re-mount), cleared on teardown.
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    // Bounded re-mount: re-fetches /stream, which for a view-time resolver
    // (Verkada) mints a fresh token server-side — so an expired-token stall
    // refreshes itself rather than dying. Shared by the hls.js ERROR handler and
    // the native-HLS (Safari) error path; returns false once the budget is spent.
    // mp4_clip gets a larger budget + slower cadence (cold-generation, #135);
    // HLS/native keeps the snappy token-refresh budget.
    const maxRetries = isMp4Clip ? MP4_COLD_RETRIES : MAX_AUTO_RETRIES;
    const retryDelayMs = isMp4Clip ? MP4_COLD_RETRY_DELAY_MS : AUTO_RETRY_DELAY_MS;
    const scheduleAutoRetry = (): boolean => {
      if (autoRetryRef.current >= maxRetries) return false;
      autoRetryRef.current += 1;
      setState('loading');
      retryTimer = setTimeout(() => {
        if (!destroyed) setAttempt((a) => a + 1);
      }, retryDelayMs);
      return true;
    };
    // True once we mount native HLS (Safari/iOS). Native playback surfaces fatal
    // failures through the <video> 'error' event (there's no hls.js to recover),
    // so — unlike hls.js — an expired Verkada token would otherwise be a dead end.
    // Give the native path the same bounded token-refresh retry. mp4_clip drives
    // its own reload timer, so it keeps the plain error→snapshot behaviour.
    let usingNativeHls = false;
    const onNativeError = (): void => {
      if (destroyed) return;
      // Native HLS (Verkada token refresh) AND mp4_clip cold-generation both get a
      // bounded auto-retry before declaring offline — for mp4_clip this is the
      // fix that stops a cold MTQ clip from reading as a broken feed (#135).
      if ((usingNativeHls || isMp4Clip) && scheduleAutoRetry()) return;
      setState('error');
    };
    video.addEventListener('playing', onPlaying);
    video.addEventListener('error', onNativeError);
    video.addEventListener('resize', onResize);

    let clipTimer: ReturnType<typeof setInterval> | undefined;

    if (isMp4Clip) {
      // Loop the current clip; re-fetch on the interval with a cache-buster so
      // the origin returns a fresh clip rather than the cached one. The reload
      // is in-place (no React state reset), so the LIVE badge doesn't flicker.
      video.loop = true;
      let bust = 0;
      const load = (): void => {
        const sep = src.includes('?') ? '&' : '?';
        video.src = bust === 0 ? src : `${src}${sep}_=${bust}`;
        video.load();
        // play() returns a Promise in browsers; guard for environments where it
        // doesn't (jsdom) so a missing .catch can't throw inside the effect.
        void video.play()?.catch(() => undefined);
      };
      load();
      clipTimer = setInterval(() => {
        if (destroyed) return;
        bust += 1;
        load();
      }, refreshMs);
      // Bound a silent cold-generation stall: if no frame ever arrives, surface
      // offline + manual retry instead of an endless spinner (#135).
      coldStartTimer = setTimeout(() => {
        if (!destroyed && !reachedPlaying) setState('error');
      }, MP4_FIRST_FRAME_TIMEOUT_MS);
    } else if (Hls.isSupported()) {
      // hls.js FIRST, native HLS only as the no-MSE fallback (#279). This order
      // matters: desktop Chrome (≥~131, observed Chrome 149) now answers
      // canPlayType('application/vnd.apple.mpegurl') with 'maybe' — its 2025
      // built-in HLS player. The old native-first order therefore routed Chrome
      // into that player, which re-fetches a LIVE playlist in a hot loop
      // (~125 req/s per tile measured, Range/206s from the media pipeline)
      // instead of honoring the playlist's target duration — six wall tiles
      // hammered our own api at ~2k req/s (the #279 storm). hls.js reloads on
      // the playlist cadence and carries the #135 bounded retries, so when MSE
      // is available it ALWAYS wins; the canPlayType branch below is the true
      // no-MSE fallback (iOS Safari), where the OS-native player behaves.
      const hls = new Hls({ enableWorker: true });
      hlsRef.current = hls;
      hls.on(Hls.Events.ERROR, (_evt, data) => {
        // Only fatal errors are unrecoverable; hls.js auto-recovers the rest.
        if (!data.fatal || destroyed) return;
        // Before giving up, re-mount the source a bounded number of times (shared
        // with the native-HLS path). For a view-time resolver (Verkada) this
        // re-fetches /stream and mints a fresh token, so an expired-token stall
        // refreshes itself rather than dying.
        if (!scheduleAutoRetry()) setState('error');
      });
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        void video.play().catch(() => undefined);
      });
      hls.loadSource(src);
      hls.attachMedia(video);
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // True native HLS — only reached with no MSE (iOS Safari). Mark the mode
      // so the shared <video> 'error' handler applies the token-refresh retry
      // (a rotating Verkada token would otherwise be a one-shot "Stream
      // offline" here).
      usingNativeHls = true;
      video.src = src;
      void video.play().catch(() => undefined);
    } else {
      setState('error');
    }

    return () => {
      destroyed = true;
      if (clipTimer) clearInterval(clipTimer);
      if (retryTimer) clearTimeout(retryTimer);
      if (noVideoTimer) clearTimeout(noVideoTimer);
      if (coldStartTimer) clearTimeout(coldStartTimer);
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('error', onNativeError);
      video.removeEventListener('resize', onResize);
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      video.pause();
      video.removeAttribute('src');
      video.load();
    };
  }, [src, attempt, isMp4Clip, refreshMs]);

  if (isYouTube) {
    return (
      <YouTubeLivePlayer
        cameraId={cameraId}
        name={name}
        enablePopout={enablePopout}
        fill={fill}
        chromeless={fill}
        onPlaybackState={onPlaybackState}
      />
    );
  }

  return (
    <div
      className={
        fill
          ? 'relative h-full w-full overflow-hidden bg-black'
          : 'relative w-full overflow-hidden rounded border border-slate-700 bg-black'
      }
      style={fill ? undefined : { aspectRatio: '16 / 9' }}
      data-testid="live-player"
      data-fill={fill ? 'true' : undefined}
    >
      <video
        ref={videoRef}
        data-testid="live-video"
        muted
        autoPlay
        playsInline
        loop={isMp4Clip}
        // HLS upstreams are CORS-open (§0) and the proxy is same-origin, so
        // anonymous CORS keeps the frame buffer readable (canvas / future frame
        // analysis). mp4_clip origins (quebec511) send no CORS header, where
        // anonymous CORS would block the media load — so leave it unset there.
        crossOrigin={isMp4Clip && !useProxy ? undefined : 'anonymous'}
        // Fill (wall mosaic) centre-crops; the panel card letterboxes.
        className={`h-full w-full bg-black ${fill ? 'object-cover' : 'object-contain'}`}
      />

      {enablePopout && (
        <button
          type="button"
          data-testid="popout-button"
          onClick={() => openPopoutWindow(cameraId)}
          title="Open in new window"
          aria-label="Open in new window"
          className="absolute right-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded bg-black/60 text-slate-200 hover:bg-black/80"
        >
          {/* up-right arrow into a box — "pop out" affordance */}
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 4h6v6" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M20 4l-8 8" strokeLinecap="round" strokeLinejoin="round" />
            <path
              d="M18 14v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}

      {state === 'playing' && !fill && (
        <div
          data-testid="live-badge"
          className="absolute left-2 top-2 flex items-center gap-1.5 rounded bg-black/60 px-2 py-1 text-[11px] font-semibold uppercase tracking-wider"
          style={{ color: COVERAGE_LIVE }}
        >
          <span
            className="inline-block h-2 w-2 animate-pulse rounded-full"
            style={{ backgroundColor: COVERAGE_LIVE }}
          />
          LIVE
        </div>
      )}

      {state === 'loading' && (
        <div
          data-testid="live-loading"
          className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-slate-300"
        >
          <span className="h-6 w-6 animate-spin rounded-full border-2 border-slate-500 border-t-transparent" />
          {isMp4Clip ? (
            // Honest cold-start copy (#135): the provider generates the clip on
            // demand, so a ~30s wait is expected behaviour, not a broken feed. The
            // provider is named from the row's source (#386) — never hardcoded —
            // so a non-QC on-demand clip (NB CCG e-Nav) is attributed correctly.
            <span className="max-w-xs px-4 text-center text-xs text-slate-300">
              {mp4ClipProviderLabel(source)
                ? `${mp4ClipProviderLabel(source)} generates this clip on demand — may take ~30s…`
                : 'This clip is generated on demand — may take ~30s…'}
            </span>
          ) : (
            <span className="text-xs">Connecting to live stream…</span>
          )}
        </div>
      )}

      {state === 'error' && (
        <div
          data-testid="live-error"
          className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-slate-950/70"
        >
          {/* Best-effort still; 404s silently for stream-only cams. */}
          <img
            src={`/api/cameras/${cameraId}/snapshot`}
            alt={`Last snapshot from ${name}`}
            className="absolute inset-0 h-full w-full object-cover opacity-40"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
          <span className="z-10 text-sm font-medium text-slate-200">Stream offline</span>
          {!fill && (
            <button
              type="button"
              onClick={() => {
                autoRetryRef.current = 0; // manual retry re-arms the auto-retry budget
                setAttempt((a) => a + 1);
              }}
              className="z-10 rounded border border-slate-600 bg-slate-800 px-3 py-1 text-xs text-slate-100 hover:bg-slate-700"
            >
              Retry stream
            </button>
          )}
        </div>
      )}

      {state === 'no_video' && (
        // Honest state: the stream is decoding (currentTime advances) but carries
        // no video track — audio-only HLS. NOT a LIVE badge over a black frame.
        <div
          data-testid="live-no-video"
          className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-slate-950/70 px-4 text-center"
        >
          {/* Best-effort still behind the notice; 404s silently for stream-only cams. */}
          <img
            src={`/api/cameras/${cameraId}/snapshot`}
            alt={`Last snapshot from ${name}`}
            className="absolute inset-0 h-full w-full object-cover opacity-30"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
          <span className="z-10 text-sm font-medium text-slate-200">No video track</span>
          <span className="z-10 max-w-xs text-xs text-slate-400">
            This source is streaming audio only — it isn&apos;t sending a video
            picture right now.
          </span>
          {!fill && (
            <button
              type="button"
              onClick={() => {
                autoRetryRef.current = 0;
                setAttempt((a) => a + 1);
              }}
              className="z-10 mt-1 rounded border border-slate-600 bg-slate-800 px-3 py-1 text-xs text-slate-100 hover:bg-slate-700"
            >
              Retry stream
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** Hosts whose live streams must go through the API proxy. Today: ipcamlive,
 *  whose manifests are plain http:// (mixed-content-blocked on https). The
 *  generic http:// check below also covers any future such source. From the
 *  Phase D.2 §0 CORS survey, every live host is CORS-open, so this is purely
 *  the mixed-content set. */
const PROXY_STREAM_HOSTS = ['ipcamlive.com'];

/** An Ozolio embed page (`…ozolio.com/…?cmd=embed&oid=…`). Not a manifest — the
 *  API resolves its stateful session to a live MJPEG stream at view time, so
 *  these always render through the same-origin MJPEG <img> player (CameraMedia),
 *  regardless of the row's declared stream_type. See apps/api/src/ozolio.ts. */
export function isOzolioEmbed(streamUrl: string | null | undefined): boolean {
  if (!streamUrl) return false;
  try {
    const u = new URL(streamUrl);
    const ozolio = u.hostname === 'ozolio.com' || u.hostname.endsWith('.ozolio.com');
    return ozolio && /[?&]cmd=embed(&|$)/i.test(u.search) && /[?&]oid=/i.test(u.search);
  } catch {
    return false;
  }
}

export function shouldProxyStream(streamUrl: string): boolean {
  try {
    const u = new URL(streamUrl);
    // Ozolio embed: resolved server-side to a session MJPEG stream and proxied
    // same-origin (the MjpegPlayer always proxies, but keep this honest too).
    if (isOzolioEmbed(streamUrl)) return true;
    // WetMet frame.php is an HTML player page, not a manifest — it must be
    // resolved server-side, so route it through /stream (which 302s the player
    // to the freshly-signed, CORS-open m3u8). See apps/api/src/wetmet.ts.
    if (/(^|\.)wetmet\.net$/i.test(u.hostname) && /frame\.php/i.test(u.pathname)) return true;
    // Verkada Command embed: an HTML 302 page, not a manifest — the server
    // resolves it to a token-bearing H.264 HLS playlist and byte-proxies it (the
    // CDN is non-CORS). Route through /stream. See apps/api/src/verkada.ts.
    if (/(^|\.)command\.verkada\.com$/i.test(u.hostname) && /\/embed(\/|\.html|$)/i.test(u.pathname))
      return true;
    // HDOnTap play-API: `hdontap.com/api/streams/<id>/play/` is a JSON page, not a
    // manifest — the server mints a fresh signed m3u8 from it per view and 302s the
    // player to the CORS-open edge. Route through /stream. See apps/api/src/hdontap.ts.
    if (/(^|\.)hdontap\.com$/i.test(u.hostname) && /^\/api\/streams\/\d+\/play\/?$/i.test(u.pathname))
      return true;
    if (u.protocol === 'http:') return true; // mixed content on https deploys
    return PROXY_STREAM_HOSTS.some((h) => u.hostname.endsWith(h));
  } catch {
    return false;
  }
}
